import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ChevronLeft, ChevronRight, 
  Ghost, Calendar as CalendarIcon,
  CloudRain, Navigation, LayoutGrid, Plus,
  ChevronDown, ShieldAlert, Check, User as UserIcon, Layers, Building2
} from 'lucide-react';
// 🛰️ Using unified shared types
import { CustomCalendar, HouseholdMember, DataScope } from '@/types';

interface ToolbarProps {
  view: 'day' | 'week' | 'month';
  onViewChange: (view: 'day' | 'week' | 'month') => void;
  onToday: () => void;
  onPrev: () => void;
  onNext: () => void;
  currentDateLabel: string;
  isTravelEnabled: boolean;
  setTravelEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isGhostLayerEnabled: boolean;
  setGhostLayerEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  activeMembers: string[];
  onMemberToggle: (id: string) => void;
  householdMembers: HouseholdMember[]; // Assuming HouseholdMember is defined
  customCalendars: CustomCalendar[]; // Assuming CustomCalendar is defined
  activeCalendarIds: string[];
  onCalendarToggle: (id: string) => void;
  onAddNewCalendar: () => void;
  hasConflicts: boolean;
  activeScope: DataScope; // 🎯 Change this line to accept DataScope directly
  onScopeChange: React.Dispatch<React.SetStateAction<DataScope>>; // Adjust if onScopeChange is different
  currentHouseholdId: string | null;
}

export const CalendarToolbar: React.FC<ToolbarProps> = (props) => {
  const { t } = useTranslation();
  const [isCalendarDropdownOpen, setIsCalendarDropdownOpen] = useState(false);

  return (
    <div className="w-full h-20 bg-white dark:bg-slate-950 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between px-6 z-[60] relative">
      
      {/* --- LEFT SLOT: NAVIGATION & CONTEXT --- */}
      <div className="flex items-center gap-4">
        <button 
          onClick={props.onToday} 
          className="px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 dark:hover:bg-slate-700 transition-all active:scale-95 shadow-sm"
        >
          {t('calendar.today', 'Today')}
        </button>
        
        <div className="flex bg-slate-50 dark:bg-slate-900 rounded-xl p-1 border border-slate-100 dark:border-slate-800">
          <button onClick={props.onPrev} className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-all text-slate-500">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={props.onNext} className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-all text-slate-500">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-col ml-2">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-black text-slate-900 dark:text-white leading-none tracking-tighter italic uppercase">
              {props.currentDateLabel}
            </h2>
            {/* 🛡️ Privacy Scope Badge */}
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
               {props.currentHouseholdId ? <Layers className="w-2 h-2 text-indigo-500" /> : <UserIcon className="w-2 h-2 text-emerald-500" />}
               <span className="text-[7px] font-black uppercase text-slate-500">{props.currentHouseholdId ? props.activeScope : 'Personal'}</span>
            </div>
          </div>
          {props.hasConflicts && (
            <div className="flex items-center gap-1 mt-1 animate-pulse">
              <ShieldAlert className="w-2.5 h-2.5 text-rose-500" />
              <span className="text-[8px] font-black text-rose-500 uppercase tracking-tighter">Temporal Conflict Detected</span>
            </div>
          )}
        </div>
      </div>

      {/* --- CENTER SLOT: VIEW SWITCHER & LAYERS --- */}
      <div className="hidden lg:flex items-center gap-6">
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-inner">
          {(['day', 'week', 'month'] as const).map((v) => (
            <button
              key={v}
              onClick={() => props.onViewChange(v)}
              className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${props.view === v ? 'bg-white dark:bg-slate-700 shadow-lg text-indigo-600' : 'text-slate-400 hover:text-slate-500'}`}
            >
              {v}
            </button>
          ))}
        </div>

        {/* 📚 Layer Manager Dropdown */}
        <div className="relative">
          <button 
            onClick={() => setIsCalendarDropdownOpen(!isCalendarDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-indigo-200 transition-all shadow-sm"
          >
            <LayoutGrid className="w-4 h-4 text-indigo-500" />
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300">Layers</span>
            <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-300 ${isCalendarDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {isCalendarDropdownOpen && (
            <>
              <div className="fixed inset-0 z-[-1]" onClick={() => setIsCalendarDropdownOpen(false)} />
              <div className="absolute top-full left-0 mt-3 w-64 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-2xl p-4 animate-in fade-in zoom-in-95 duration-200">
                <div className="space-y-1">
                  {/* 🛡️ Personal Layer (Always Visible) */}
                  <CalendarLayerItem 
                    label="Personal Cloud" 
                    color="bg-emerald-500" 
                    isActive={props.activeCalendarIds.includes('personal')} 
                    onClick={() => props.onCalendarToggle('personal')} 
                  />
                  
                  {/* 🏢 Household Layer (Context Conditional) */}
                  {props.currentHouseholdId && (
                    <CalendarLayerItem 
                      label="Household Hub" 
                      color="bg-indigo-500" 
                      isActive={props.activeCalendarIds.includes('household')} 
                      onClick={() => props.onCalendarToggle('household')} 
                    />
                  )}

                  <div className="h-px bg-slate-100 dark:bg-slate-800 my-2" />
                  
                  <div className="max-h-40 overflow-y-auto no-scrollbar space-y-1">
                    {props.customCalendars.map((cal: CustomCalendar) => (
                      <CalendarLayerItem 
                        key={cal.id}
                        label={cal.title}
                        color={cal.color}
                        isActive={props.activeCalendarIds.includes(cal.id)}
                        onClick={() => props.onCalendarToggle(cal.id)}
                      />
                    ))}
                  </div>
                </div>
                
                <button 
                  onClick={() => {
                    props.onAddNewCalendar();
                    setIsCalendarDropdownOpen(false);
                  }}
                  className="w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 text-[10px] font-black uppercase text-slate-400 hover:text-indigo-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
                >
                  <Plus className="w-3 h-3" /> New Calendar
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* --- RIGHT SLOT: INTELLIGENCE & MEMBERS --- */}
      <div className="flex items-center gap-3">
        <div className="hidden xl:flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 mr-2 shadow-sm">
          <CloudRain className="w-4 h-4 text-blue-400" />
          <span className="text-[10px] font-black text-slate-500 uppercase tracking-tighter">Rain @ 2PM</span>
        </div>

        <button 
          onClick={() => props.setTravelEnabled(!props.isTravelEnabled)}
          className={`p-3 rounded-2xl transition-all border ${props.isTravelEnabled ? 'bg-indigo-50 border-indigo-200 text-indigo-600 shadow-lg' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-slate-400'}`}
          title="Travel Buffer Logic"
        >
          <Navigation className={`w-5 h-5 ${props.isTravelEnabled ? 'animate-pulse text-indigo-600' : ''}`} />
        </button>

        <button 
          onClick={() => props.setGhostLayerEnabled(!props.isGhostLayerEnabled)}
          className={`p-3 rounded-2xl transition-all border ${props.isGhostLayerEnabled ? 'bg-amber-50 border-amber-200 text-amber-600 shadow-lg' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-slate-400'}`}
          title="Ghost Task Layer"
        >
          <Ghost className="w-5 h-5" />
        </button>

        <div className="h-8 w-px bg-slate-100 dark:bg-slate-800 mx-2" />

        {/* 👥 Member Perspective (Only shown if Household is active) */}
        <div className="flex -space-x-2">
          {props.currentHouseholdId && props.householdMembers.map((member: HouseholdMember) => (
            <button 
              key={member.userId} 
              onClick={() => props.onMemberToggle(member.userId)}
              className={`w-10 h-10 rounded-full border-2 border-white dark:border-slate-950 flex items-center justify-center overflow-hidden transition-all relative ${props.activeMembers.includes(member.userId) ? 'ring-2 ring-indigo-500 scale-110 z-10' : 'opacity-40 grayscale hover:opacity-100 hover:scale-105'}`}
            >
              {member.avatar ? (
                <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" />
              ) : (
                <div className="bg-slate-200 dark:bg-slate-700 w-full h-full flex items-center justify-center font-bold text-[10px] text-slate-500">
                  {member.name[0]}
                </div>
              )}
            </button>
          ))}
          {props.currentHouseholdId && (
            <button className="w-10 h-10 rounded-full bg-slate-50 dark:bg-slate-900 border-2 border-dashed border-slate-200 dark:border-slate-800 flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all active:scale-95">
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* --- SUB-COMPONENT: CALENDAR LAYER ITEM --- */
interface CalendarLayerItemProps {
  label: string;
  color: string;
  isActive: boolean;
  onClick: () => void;
}

const CalendarLayerItem: React.FC<CalendarLayerItemProps> = ({ label, color, isActive, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
  >
    <div className="flex items-center gap-3">
      <div className={`w-3 h-3 rounded-full ${color} shadow-sm transition-transform group-hover:scale-110`} />
      <span className="text-[11px] font-black uppercase text-slate-600 dark:text-slate-300 tracking-widest">{label}</span>
    </div>
    {isActive && (
      <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center animate-in zoom-in duration-200">
        <Check className="w-3 h-3 text-white" />
      </div>
    )}
  </button>
);